/**
 * CompanionToolsRow — Feb 2026.
 *
 * Surfaces the blank, teacher-fillable companion PDFs alongside a
 * Classroom Support plan (e.g. checklist, visual schedule, first-then
 * board, reward chart). Detection runs on the backend (Claude call,
 * persisted on the plan doc); this component just fetches the list,
 * renders a chip per tool, and downloads the matching PDF on click.
 *
 * Renders NOTHING when:
 *   - no `planId` is provided
 *   - the backend returns 0 recommended tools
 *   - the detect call fails (defensive — keeps the surrounding UI clean)
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckSquare,
  CalendarDays,
  ArrowRightLeft,
  Award,
  Download,
  Loader2,
} from "lucide-react";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TOOL_META = {
  checklist: { label: "Checklist", Icon: CheckSquare },
  visual_schedule: { label: "Visual schedule", Icon: CalendarDays },
  first_then: { label: "First-then board", Icon: ArrowRightLeft },
  reward_chart: { label: "Reward chart", Icon: Award },
};

export default function CompanionToolsRow({ planId, variant = "card", testidPrefix = "companion-tools" }) {
  const [tools, setTools] = useState(null);   // null = unknown, [] = none
  const [loading, setLoading] = useState(true);
  const [downloadingType, setDownloadingType] = useState(null);

  const load = useCallback(async () => {
    if (!planId) {
      setTools([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(
        `${apiBase()}/classroom-support/plans/${planId}/companion-tools`,
        { credentials: "include" },
      );
      if (!r.ok) {
        setTools([]);
        return;
      }
      const data = await r.json();
      const list = Array.isArray(data?.tools) ? data.tools : [];
      setTools(list.filter((t) => TOOL_META[t]));
    } catch {
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (toolType) => {
    setDownloadingType(toolType);
    try {
      const r = await fetch(
        `${apiBase()}/classroom-support/plans/${planId}/companion/${toolType}.pdf`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `companion-${toolType}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || "Couldn't download the companion tool.");
    } finally {
      setDownloadingType(null);
    }
  };

  if (!planId) return null;
  if (loading) {
    if (variant === "inline") return null;
    return (
      <div
        className="mt-3 text-xs text-[#4a6984]/70 inline-flex items-center gap-1.5"
        data-testid={`${testidPrefix}-loading`}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Detecting companion tools…
      </div>
    );
  }
  if (!tools || tools.length === 0) return null;

  if (variant === "inline") {
    return (
      <div
        className="mt-2.5 flex flex-wrap items-center gap-1.5"
        data-testid={`${testidPrefix}-row`}
      >
        <span className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/70 mr-1">
          Companion tools
        </span>
        {tools.map((t) => {
          const meta = TOOL_META[t];
          const Icon = meta.Icon;
          const busy = downloadingType === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => download(t)}
              disabled={busy}
              data-testid={`${testidPrefix}-download-${t}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#4a6984]/25 text-[#1c2d3d] text-[11px] font-extrabold hover:bg-[#96ded1]/15 transition disabled:opacity-50"
              title={`Download blank ${meta.label} (PDF)`}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Icon className="h-3 w-3" />
              )}
              {meta.label}
            </button>
          );
        })}
      </div>
    );
  }

  // Default "card" variant — full-width band rendered on the generator.
  return (
    <section
      className="mt-5 ae-card p-5 sm:p-6 bg-white border border-[#96ded1]/40"
      data-testid={`${testidPrefix}-card`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-1">
            Companion tools the plan recommends
          </p>
          <h3 className="font-extrabold text-base text-[#1c2d3d]">
            Blank, teacher-fillable. Print and use right away.
          </h3>
        </div>
      </div>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
        data-testid={`${testidPrefix}-grid`}
      >
        {tools.map((t) => {
          const meta = TOOL_META[t];
          const Icon = meta.Icon;
          const busy = downloadingType === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => download(t)}
              disabled={busy}
              data-testid={`${testidPrefix}-download-${t}`}
              className="flex items-center gap-3 p-3 rounded-lg border border-[#4a6984]/15 hover:border-[#96ded1] hover:bg-[#96ded1]/10 transition text-left disabled:opacity-50"
            >
              <span className="h-9 w-9 rounded-md bg-[#96ded1]/25 grid place-items-center flex-shrink-0">
                <Icon className="h-4 w-4 text-[#1c2d3d]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-extrabold text-sm text-[#1c2d3d] truncate">
                  {meta.label}
                </span>
                <span className="block text-[11px] text-[#4a6984]/75">
                  PDF · blank template
                </span>
              </span>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#4a6984]" />
              ) : (
                <Download className="h-4 w-4 text-[#4a6984]/70" />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
